import React, { useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, Leaf, AlertTriangle, Sparkles, Search, Activity, ArrowUpRight, FileText, Download } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MiniSparkline } from '../components/charts/MiniSparkline';
// --- MOCK DATA ---
const generateDailyEmissions = () => {
  const data = [];
  const today = new Date();
  for (let i = 30; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    data.push({
      date: date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'short'
      }),
      OpenAI: Number((3 + Math.random() * 1.5).toFixed(2)),
      Anthropic: Number((1.5 + Math.random() * 1).toFixed(2)),
      Google: Number((0.3 + Math.random() * 0.5).toFixed(2))
    });
  }
  return data;
};
const DAILY_EMISSIONS = generateDailyEmissions();
const SERVICES_IMPACT = [{
  id: 's1',
  name: 'Customer Assistant',
  provider: 'OpenAI',
  model: 'GPT-4o',
  impact: 42,
  percent: 33.9,
  trend: [40, 42, 41, 43, 45, 44, 42],
  status: 'warning'
}, {
  id: 's2',
  name: 'Resume Screener',
  provider: 'OpenAI',
  model: 'GPT-4',
  impact: 31,
  percent: 25.0,
  trend: [20, 22, 25, 28, 30, 32, 31],
  status: 'danger'
}, {
  id: 's3',
  name: 'Smart Matcher',
  provider: 'Anthropic',
  model: 'Claude 3.5 Sonnet',
  impact: 22,
  percent: 17.7,
  trend: [25, 24, 23, 22, 21, 22, 22],
  status: 'success'
}, {
  id: 's4',
  name: 'Document Analyzer',
  provider: 'Google',
  model: 'Gemini Pro',
  impact: 16,
  percent: 12.9,
  trend: [15, 16, 15, 16, 17, 16, 16],
  status: 'success'
}, {
  id: 's5',
  name: 'Content Moderator',
  provider: 'OpenAI',
  model: 'GPT-4o-mini',
  impact: 8,
  percent: 6.5,
  trend: [10, 9, 8, 9, 8, 7, 8],
  status: 'success'
}];
const EFFICIENCY_DATA = [{
  model: 'GPT-4o-mini',
  provider: 'OpenAI',
  consumption: '0,15 Wh',
  co2: '0,07 kg',
  efficiency: 'Ottima',
  status: 'success'
}, {
  model: 'GPT-4o',
  provider: 'OpenAI',
  consumption: '0,30 Wh',
  co2: '0,14 kg',
  efficiency: 'Buona',
  status: 'success'
}, {
  model: 'Claude 3.5 Sonnet',
  provider: 'Anthropic',
  consumption: '0,35 Wh',
  co2: '0,16 kg',
  efficiency: 'Media',
  status: 'warning'
}, {
  model: 'Gemini Pro',
  provider: 'Google',
  consumption: '0,40 Wh',
  co2: '0,18 kg',
  efficiency: 'Media',
  status: 'warning'
}, {
  model: 'GPT-4',
  provider: 'OpenAI',
  consumption: '1,20 Wh',
  co2: '0,55 kg',
  efficiency: 'Bassa',
  status: 'danger'
}];
const PROVIDER_IMPACT = [{
  name: 'OpenAI',
  color: '#00B4D8',
  co2: 81,
  percent: 65.3,
  models: 3,
  topModel: 'GPT-4o',
  trend: '-12%'
}, {
  name: 'Anthropic',
  color: '#2A3B5C',
  co2: 22,
  percent: 17.7,
  models: 1,
  topModel: 'Claude 3.5 Sonnet',
  trend: '-8%'
}, {
  name: 'Google',
  color: '#10b981',
  co2: 16,
  percent: 12.9,
  models: 1,
  topModel: 'Gemini Pro',
  trend: '-3%'
}];
export function GreenOpsPage() {
  const [activeTab, setActiveTab] = useState('Panoramica');
  const [period, setPeriod] = useState('30G');
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = useMemo(() => {
    return SERVICES_IMPACT.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.provider.toLowerCase().includes(searchQuery.toLowerCase()) || s.model.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [searchQuery]);
  return <div className="min-h-screen bg-[#F4F6F8] p-4 md:p-8" data-id="element-3071">
      {/* 1. HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8" data-id="element-3072">
        <div data-id="element-3073">
          <h1 className="text-[28px] md:text-[32px] font-semibold text-[#1A202C] tracking-tight" data-id="element-3074">
            GreenOps
          </h1>
          <p className="text-[14px] text-gray-600 mt-1" data-id="element-3075">
            Dashboard di monitoraggio dell'impatto ambientale dei servizi AI.
          </p>
        </div>
        <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm" data-id="element-3076">
          {['7G', '30G', '90G', '12M'].map(p => <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors ${period === p ? 'bg-[#F4F6F8] text-[#2A3B5C] shadow-sm' : 'text-gray-500 hover:text-[#1A202C]'}`} data-id="element-3077">
              {p}
            </button>)}
        </div>
      </div>

      {/* 2. KPI CARDS ROW */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-3078">
        {/* Card 1 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-3079">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-3080">
            Impatto CO₂ Totale Mese
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-3081">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-3082">
              124 kg CO₂
            </span>
            <span className="text-[12px] text-emerald-600 font-medium flex items-center gap-0.5" data-id="element-3083">
              <TrendingDown className="w-3.5 h-3.5" data-id="element-3084" /> -15%
            </span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1" data-id="element-3085">vs mese precedente</p>
        </div>

        {/* Card 2 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-3086">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-3087">
            Impatto Medio per Query
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-3088">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-3089">
              0,42 Wh
            </span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1" data-id="element-3090">
            vs 0,48 Wh mese prec.
          </p>
        </div>

        {/* Card 3 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-3091">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-3092">
            Servizio più Green
          </h3>
          <div className="flex items-baseline gap-2 mt-auto flex-wrap" data-id="element-3093">
            <span className="text-[20px] font-bold text-[#1A202C] leading-tight" data-id="element-3094">
              Content Moderator
            </span>
            <span className="bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full px-2 py-0.5 text-[10px] font-medium" data-id="element-3095">
              Più efficiente
            </span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1" data-id="element-3096">GPT-4o-mini</p>
        </div>

        {/* Card 4 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-3097">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-3098">
            Potenziale Risparmio
          </h3>
          <div className="mt-auto" data-id="element-3099">
            <div className="flex items-baseline gap-1 mb-2" data-id="element-3100">
              <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-3101">
                -18 kg CO₂/mese
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1" data-id="element-3102">
              <div className="bg-amber-500 h-1.5 rounded-full" style={{
              width: '45%'
            }} data-id="element-3103"></div>
            </div>
            <p className="text-[10px] text-gray-400 leading-tight" data-id="element-3104">
              Applicando le ottimizzazioni suggerite
            </p>
          </div>
        </div>
      </div>

      {/* 3. TABS NAVIGATION */}
      <div className="border-b-2 border-gray-300 mb-6" data-id="element-3105">
        <div className="-mb-[2px] flex items-center gap-8 overflow-x-auto custom-scrollbar" data-id="element-3106">
          {['Panoramica', 'Per Servizio', 'Per Provider'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-[15px] font-medium transition-colors whitespace-nowrap border-b-2 ${activeTab === tab ? 'border-[#00B4D8] text-[#2A3B5C]' : 'border-transparent text-gray-500 hover:text-gray-800'}`} data-id="element-3107">
              {tab}
            </button>)}
        </div>
      </div>

      {/* 4. TAB CONTENT */}
      {activeTab === 'Panoramica' && <div className="space-y-6" data-id="element-3108">
          {/* ROW 3: Chart & Ranking */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3109">
            {/* Area Chart */}
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-id="element-3110">
              <div className="flex justify-between items-center mb-6" data-id="element-3111">
                <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3112">
                  Trend Impatto CO₂ Mensile
                </h2>
                <div className="flex items-center gap-4 text-[11px] font-medium" data-id="element-3113">
                  <div className="flex items-center gap-1.5" data-id="element-3114">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#00B4D8]" data-id="element-3115"></div>
                    OpenAI
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-3116">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#2A3B5C]" data-id="element-3117"></div>
                    Anthropic
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-3118">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#10b981]" data-id="element-3119"></div>
                    Google
                  </div>
                </div>
              </div>
              <div className="h-[360px] w-full" data-id="element-3120">
                <ResponsiveContainer width="100%" height="100%" data-id="element-3121">
                  <AreaChart data={DAILY_EMISSIONS} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-3122">
                    <defs data-id="element-3123">
                      <linearGradient id="greenColorOpenAI" x1="0" y1="0" x2="0" y2="1" data-id="element-3124">
                        <stop offset="5%" stopColor="#00B4D8" stopOpacity={0.3} data-id="element-3125" />
                        <stop offset="95%" stopColor="#00B4D8" stopOpacity={0} data-id="element-3126" />
                      </linearGradient>
                      <linearGradient id="greenColorAnthropic" x1="0" y1="0" x2="0" y2="1" data-id="element-3127">
                        <stop offset="5%" stopColor="#2A3B5C" stopOpacity={0.3} data-id="element-3128" />
                        <stop offset="95%" stopColor="#2A3B5C" stopOpacity={0} data-id="element-3129" />
                      </linearGradient>
                      <linearGradient id="greenColorGoogle" x1="0" y1="0" x2="0" y2="1" data-id="element-3130">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} data-id="element-3131" />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-3132" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-3133" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#6B7280'
                }} dy={10} minTickGap={30} data-id="element-3134" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#6B7280'
                }} tickFormatter={val => `${val} kg`} domain={[0, 6]} data-id="element-3135" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                  fontSize: '12px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }} itemStyle={{
                  color: '#1A202C',
                  fontWeight: 500
                }} formatter={(value: number) => [`${value} kg CO₂`, undefined]} data-id="element-3136" />
                    <Area type="monotone" dataKey="Google" stackId="1" stroke="#10b981" fill="url(#greenColorGoogle)" strokeWidth={2} data-id="element-3137" />
                    <Area type="monotone" dataKey="Anthropic" stackId="1" stroke="#2A3B5C" fill="url(#greenColorAnthropic)" strokeWidth={2} data-id="element-3138" />
                    <Area type="monotone" dataKey="OpenAI" stackId="1" stroke="#00B4D8" fill="url(#greenColorOpenAI)" strokeWidth={2} data-id="element-3139" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Top Services */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col" data-id="element-3140">
              <div className="flex justify-between items-center mb-6" data-id="element-3141">
                <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3142">
                  Top Servizi per Impatto
                </h2>
                <button className="text-[12px] text-[#00B4D8] font-medium hover:underline" data-id="element-3143">
                  Vedi tutti
                </button>
              </div>
              <div className="flex-1 flex flex-col gap-4" data-id="element-3144">
                {SERVICES_IMPACT.map((service, idx) => <div key={service.id} className="flex items-center justify-between group" data-id="element-3145">
                    <div className="flex items-center gap-3 overflow-hidden" data-id="element-3146">
                      <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500 shrink-0" data-id="element-3147">
                        {idx + 1}
                      </div>
                      <div className="min-w-0" data-id="element-3148">
                        <p className="text-[13px] font-medium text-[#1A202C] truncate" data-id="element-3149">
                          {service.name}
                        </p>
                        <p className="text-[11px] text-gray-500 truncate" data-id="element-3150">
                          {service.model}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0 pl-2" data-id="element-3151">
                      <div className="w-16 hidden sm:block" data-id="element-3152">
                        <MiniSparkline data={service.trend} color={service.status === 'danger' ? '#ef4444' : '#00B4D8'} className="h-[16px]" data-id="element-3153" />
                      </div>
                      <div className="text-right" data-id="element-3154">
                        <p className="text-[13px] font-semibold text-[#1A202C]" data-id="element-3155">
                          {service.impact} kg
                        </p>
                        <p className="text-[10px] text-gray-400" data-id="element-3156">
                          {service.percent}%
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* ROW 4: Efficiency Table */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-3157">
            <div className="px-6 py-5 border-b border-gray-100" data-id="element-3158">
              <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3159">
                Comparazione Efficienza Modelli
              </h2>
              <p className="text-[13px] text-gray-500 mt-1" data-id="element-3160">
                Stesso task, impatto diverso
              </p>
            </div>
            <div className="overflow-x-auto" data-id="element-3161">
              <table className="w-full text-left border-collapse" data-id="element-3162">
                <thead data-id="element-3163">
                  <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-3164">
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3165">
                      Modello
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3166">
                      Provider
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3167">
                      Consumo (Wh/query)
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3168">
                      CO₂/1000 query
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-3169">
                      Efficienza
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-3170">
                  {EFFICIENCY_DATA.map((row, idx) => <tr key={idx} className="hover:bg-gray-50/50 transition-colors" data-id="element-3171">
                      <td className="px-6 py-4 text-[13px] font-medium text-[#2a3a54]" data-id="element-3172">
                        {row.model}
                      </td>
                      <td className="px-6 py-4 text-[13px] text-gray-600" data-id="element-3173">
                        {row.provider}
                      </td>
                      <td className="px-6 py-4 text-[13px] text-gray-600 text-right" data-id="element-3174">
                        {row.consumption}
                      </td>
                      <td className="px-6 py-4 text-[13px] text-gray-600 text-right" data-id="element-3175">
                        {row.co2}
                      </td>
                      <td className="px-6 py-4 text-center" data-id="element-3176">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${row.status === 'success' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : row.status === 'warning' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-red-50 text-red-600 border-red-100'}`} data-id="element-3177">
                          {row.status === 'success' && '🟢 '}
                          {row.status === 'warning' && '🟡 '}
                          {row.status === 'danger' && '🔴 '}
                          {row.efficiency}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 bg-gray-50/50 border-t border-gray-100" data-id="element-3178">
              <p className="text-[11px] text-gray-400" data-id="element-3179">
                Stime basate su benchmark pubblici. Consumo varia in base a data
                center e mix energetico regionale.
              </p>
            </div>
          </div>

          {/* ROW 5: Suggestions & Reports */}
          <h2 className="text-[16px] font-semibold text-[#1A202C] mt-8 mb-4" data-id="element-3180">
            Suggerimenti & Report
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3181">
            {/* Card 1 */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-start gap-4 shadow-sm" data-id="element-3182">
              <div className="p-2 bg-emerald-50 rounded-lg shrink-0 mt-0.5" data-id="element-3183">
                <Leaf className="w-5 h-5 text-emerald-600" data-id="element-3184" />
              </div>
              <div className="flex-1" data-id="element-3185">
                <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-3186">
                  <h3 className="text-[14px] font-semibold text-[#1A202C]" data-id="element-3187">
                    Ottimizzazione Green
                  </h3>
                  <span className="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide border border-emerald-100" data-id="element-3188">
                    RISPARMIO STIMATO: -31 KG CO₂/MESE
                  </span>
                </div>
                <p className="text-[13px] text-gray-600 leading-relaxed mb-4" data-id="element-3189">
                  Il servizio <strong data-id="element-3190">Resume Screener</strong> utilizza GPT-4
                  (1,20 Wh/query). Passando a <strong data-id="element-3191">GPT-4o</strong> potresti
                  ridurre l'impatto ambientale del 75% mantenendo performance
                  comparabili.
                </p>
                <button className="text-[12px] font-medium text-[#00B4D8] hover:text-[#0096b4] flex items-center gap-1" data-id="element-3192">
                  Applica modifica <ArrowUpRight className="w-3.5 h-3.5" data-id="element-3193" />
                </button>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-start gap-4 shadow-sm" data-id="element-3194">
              <div className="p-2 bg-blue-50 rounded-lg shrink-0 mt-0.5" data-id="element-3195">
                <FileText className="w-5 h-5 text-[#00B4D8]" data-id="element-3196" />
              </div>
              <div className="flex-1 flex flex-col h-full" data-id="element-3197">
                <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-3198">
                  <h3 className="text-[14px] font-semibold text-[#1A202C]" data-id="element-3199">
                    Report Sostenibilità
                  </h3>
                  <span className="bg-blue-50 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide border border-blue-100" data-id="element-3200">
                    PRONTO PER IL BILANCIO
                  </span>
                </div>
                <p className="text-[13px] text-gray-600 leading-relaxed mb-4" data-id="element-3201">
                  Genera il report di impatto ambientale dei tuoi servizi AI.
                  Include metriche per provider, trend mensili e comparazione
                  modelli. Disponibile in CSV e PDF.
                </p>
                <div className="mt-auto" data-id="element-3202">
                  <button className="flex items-center justify-center px-4 py-2 bg-[#2A3B5C] text-white rounded-md hover:bg-[#1E2A40] transition-colors gap-2" data-id="element-3203">
                    <span className="text-[13px] font-heading uppercase tracking-wide whitespace-nowrap" data-id="element-3204">
                      Genera Report
                    </span>
                    <ArrowUpRight className="w-3.5 h-3.5" data-id="element-3205" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* TAB 2: PER SERVIZIO */}
      {activeTab === 'Per Servizio' && <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col" data-id="element-3206">
          <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-3207">
            <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3208">
              Dettaglio Impatto per Servizio
            </h2>
            <div className="relative w-full sm:w-72" data-id="element-3209">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-3210" />
              <input type="text" placeholder="Cerca servizio, provider o modello..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-[13px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8] transition-all" data-id="element-3211" />
            </div>
          </div>
          <div className="overflow-x-auto" data-id="element-3212">
            <table className="w-full text-left border-collapse" data-id="element-3213">
              <thead data-id="element-3214">
                <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-3215">
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3216">
                    Servizio
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3217">
                    Provider & Modello
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3218">
                    CO₂ (kg/mese)
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3219">
                    % Totale
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-3220">
                    Trend (7gg)
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-3221">
                    Efficienza
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-3222">
                {filteredServices.map(service => <tr key={service.id} className="hover:bg-gray-50/50 transition-colors group cursor-pointer" data-id="element-3223">
                    <td className="px-6 py-4" data-id="element-3224">
                      <div className="flex items-center gap-3" data-id="element-3225">
                        <div className={`w-2 h-2 rounded-full ${service.status === 'danger' ? 'bg-red-500' : service.status === 'warning' ? 'bg-amber-500' : 'bg-emerald-500'}`} data-id="element-3226"></div>
                        <span className="text-[13px] font-medium text-[#2a3a54]" data-id="element-3227">
                          {service.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-3228">
                      <div className="flex flex-col" data-id="element-3229">
                        <span className="text-[13px] text-[#1A202C] font-medium" data-id="element-3230">
                          {service.provider}
                        </span>
                        <span className="text-[11px] text-gray-500" data-id="element-3231">
                          {service.model}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-3232">
                      <span className="text-[13px] font-semibold text-[#1A202C]" data-id="element-3233">
                        {service.impact} kg
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-3234">
                      <span className="text-[13px] text-gray-600" data-id="element-3235">
                        {service.percent}%
                      </span>
                    </td>
                    <td className="px-6 py-4 w-32" data-id="element-3236">
                      <MiniSparkline data={service.trend} color={service.status === 'danger' ? '#ef4444' : '#00B4D8'} className="h-[24px]" data-id="element-3237" />
                    </td>
                    <td className="px-6 py-4 text-center" data-id="element-3238">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium border ${service.status === 'success' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : service.status === 'warning' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-red-50 text-red-600 border-red-100'}`} data-id="element-3239">
                        {service.status === 'success' ? 'Efficiente' : service.status === 'warning' ? 'Nella media' : 'Inefficiente'}
                      </span>
                    </td>
                  </tr>)}
                {filteredServices.length === 0 && <tr data-id="element-3240">
                    <td colSpan={6} className="px-6 py-8 text-center text-[13px] text-gray-500" data-id="element-3241">
                      Nessun servizio trovato per "{searchQuery}"
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>}

      {/* TAB 3: PER PROVIDER */}
      {activeTab === 'Per Provider' && <div className="space-y-6" data-id="element-3242">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-3243">
            {PROVIDER_IMPACT.map(provider => <div key={provider.name} className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex flex-col" data-id="element-3244">
                <div className="flex items-center gap-2 mb-4" data-id="element-3245">
                  <div className="w-3 h-3 rounded-full" style={{
              backgroundColor: provider.color
            }} data-id="element-3246"></div>
                  <h3 className="text-[15px] font-semibold text-[#1A202C]" data-id="element-3247">
                    {provider.name}
                  </h3>
                </div>
                <div className="mb-4" data-id="element-3248">
                  <p className="text-[24px] font-bold text-[#1A202C]" data-id="element-3249">
                    {provider.co2} kg CO₂
                  </p>
                  <p className="text-[12px] font-medium mt-1 text-emerald-500" data-id="element-3250">
                    {provider.trend} vs mese prec.
                  </p>
                </div>
                <div className="mt-auto space-y-2 pt-4 border-t border-gray-100" data-id="element-3251">
                  <div className="flex justify-between items-center" data-id="element-3252">
                    <span className="text-[12px] text-gray-500" data-id="element-3253">
                      Quota impatto
                    </span>
                    <span className="text-[12px] font-medium text-[#1A202C]" data-id="element-3254">
                      {provider.percent}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-3255">
                    <span className="text-[12px] text-gray-500" data-id="element-3256">
                      Modelli attivi
                    </span>
                    <span className="text-[12px] font-medium text-[#1A202C]" data-id="element-3257">
                      {provider.models}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-3258">
                    <span className="text-[12px] text-gray-500" data-id="element-3259">
                      Modello top
                    </span>
                    <span className="text-[12px] font-medium text-[#1A202C]" data-id="element-3260">
                      {provider.topModel}
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </div>}
    </div>;
}